import { useAppStore } from '../stores/app.js'

export class AuthService {
  constructor() {
    // Non istanziamo lo store qui, lo faremo quando necessario
    this._store = null
  }

  get appStore() {
    if (!this._store) {
      this._store = useAppStore()
    }
    return this._store
  }

  /**
   * Genera l'URL per l'autorizzazione OAuth2
   */
  async getAuthUrl() {
    // Assicuriamoci di avere la configurazione del server
    if (!this.appStore.hasServerConfig) {
      await this.appStore.fetchServerConfig()
    }

    const authConfig = this.appStore.getAuthConfig
    if (!authConfig) {
      throw new Error('Configurazione OAuth2 non disponibile')
    }

    // Genera un state random per sicurezza
    const state = this.generateRandomString(32)
    const nonce = this.generateRandomString(32)

    // Salva state e nonce in sessionStorage: sono one-time use, non devono persistere
    sessionStorage.setItem('oauth_state', state)
    sessionStorage.setItem('oauth_nonce', nonce)

    const params = new URLSearchParams({
      client_id: authConfig.client_id,
      redirect_uri: authConfig.redirect_uri,
      response_type: authConfig.response_type,
      scope: authConfig.scopes.join(' '),
      state: state,
      nonce: nonce
    })

    return `${authConfig.auth_url}?${params.toString()}`
  }

  /**
   * Avvia il processo di login reindirizzando a Keycloak
   */
  async login() {
    try {
      const authUrl = await this.getAuthUrl()
      window.location.href = authUrl
    } catch (error) {
      console.error('Errore durante il login:', error)
      throw error
    }
  }

  /**
   * Verifica se l'utente è autenticato.
   * I token sono salvati in localStorage (vedi appStore.setTokens).
   */
  isAuthenticated() {
    const token = localStorage.getItem('access_token')
    const expiresAt = localStorage.getItem('expires_at')

    if (!token || !expiresAt) {
      return false
    }

    return Date.now() < parseInt(expiresAt)
  }

  /**
   * Ottiene l'access token corrente da localStorage
   */
  getAccessToken() {
    if (this.isAuthenticated()) {
      return localStorage.getItem('access_token')
    }
    return null
  }

  /**
   * Effettua il logout
   */
  async logout() {
    const authConfig = this.appStore.getAuthConfig
    const idToken = localStorage.getItem('id_token')

    // Pulisci i dati da localStorage (coerente con setTokens)
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('expires_at')

    this.appStore.logout()

    // Redirect a Keycloak per il logout completo
    if (authConfig && authConfig.logout_url && idToken) {
      const params = new URLSearchParams({
        id_token_hint: idToken,
        post_logout_redirect_uri: window.location.origin
      })
      
      window.location.href = `${authConfig.logout_url}?${params.toString()}`
    }
  }

  /**
   * Genera una stringa casuale per state e nonce
   */
  generateRandomString(length) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  /**
   * Tenta di ripristinare la sessione utente se esiste un token valido
   */
  async restoreSession() {
    if (this.isAuthenticated()) {
      try {
        // Il token è valido, ma non abbiamo più bisogno di fare una chiamata
        // per ottenere le informazioni utente perché le abbiamo già salvate nello store
        const userData = this.appStore.user
        if (userData) {
          return userData
        }
      } catch (error) {
        console.error('Errore nel ripristino della sessione:', error)
        // Token non valido, pulisci tutto
        this.logout()
      }
    }
    return null
  }
}

// Esporta un'istanza singleton
export const authService = new AuthService()